import { consoleRoutes, WEBHOOK_DELIVERY_MODES, WEBHOOK_EVENTS, WEBHOOK_FILTER_CHANNELS, WEBHOOK_FILTER_ROLES, type WebhookDeliveryMode, type WebhookEventKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Checkbox, Segmented } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { deliveryPaused, EventDeliveryNotice, receivesEvents, type EventDelivery } from "./event-delivery-notice";

type FilterChannel = (typeof WEBHOOK_FILTER_CHANNELS)[number];
type FilterRole = (typeof WEBHOOK_FILTER_ROLES)[number];

export interface WebhookFiltersValue {
  agent_ids: string[];
  channels: FilterChannel[];
  roles: FilterRole[];
}
export const NO_FILTERS: WebhookFiltersValue = { agent_ids: [], channels: [], roles: [] };

/** Stored filters (`WebhookOut.filters`) as the editor's value. */
export function filtersFrom(raw: Record<string, unknown> | undefined): WebhookFiltersValue {
  const list = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []);
  const channels = WEBHOOK_FILTER_CHANNELS.filter((c) => list(raw?.channels).includes(c));
  const roles = WEBHOOK_FILTER_ROLES.filter((r) => list(raw?.roles).includes(r));
  return { agent_ids: list(raw?.agent_ids), channels, roles };
}

export const deliveryModeOf = (raw: string | undefined): WebhookDeliveryMode => WEBHOOK_DELIVERY_MODES.find((mode) => mode === raw) ?? "events";

function toggle<T extends string>(list: readonly T[], value: T, on: boolean, order: readonly T[]): T[] {
  return on ? order.filter((item) => item === value || list.includes(item)) : list.filter((item) => item !== value);
}

function ChoiceChips<T extends string>({ legend, options, value, label, onChange }: { legend: string; options: readonly T[]; value: T[]; label: (option: T) => string; onChange: (value: T[]) => void }) {
  return (
    <fieldset>
      <legend className="label">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <div key={option} className="rounded-md border border-line px-3 py-1.5">
            <Checkbox checked={value.includes(option)} onCheckedChange={(checked) => { onChange(toggle(value, option, checked === true, options)); }} label={<span className="text-xs font-semibold">{label(option)}</span>} />
          </div>
        ))}
      </div>
    </fieldset>
  );
}

interface DeliveryFieldProps {
  mode: WebhookDeliveryMode;
  filters: WebhookFiltersValue;
  events: WebhookEventKey[];
  /** The Developer webhooks workflow that sends the events: said to be the sender, and warned about when it is paused. */
  eventDelivery?: EventDelivery;
  onModeChange: (mode: WebhookDeliveryMode) => void;
  onFiltersChange: (filters: WebhookFiltersValue) => void;
  onEventsChange: (events: WebhookEventKey[]) => void;
}

/** When the endpoint is called (DEC-083): every subscribed event, a filtered set, or only from workflows. */
export function WebhookDeliveryField({ mode, filters, events, eventDelivery = null, onModeChange, onFiltersChange, onEventsChange }: DeliveryFieldProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const P = "settings.webhooks.delivery";
  return (
    <div className="space-y-4">
      <div>
        <span className="label">{t(`${P}.label`)}</span>
        <Segmented aria-label={t(`${P}.label`)} value={mode} onValueChange={onModeChange} options={WEBHOOK_DELIVERY_MODES.map((value) => ({ value, label: t(`${P}.modes.${value}`) }))} />
        <p className="mt-1.5 text-xs text-fg-subtle">{t(`${P}.hints.${mode}`)}</p>
        {receivesEvents(mode) ? (
          <p className="mt-1 text-xs text-fg-subtle">
            {t(`${P}.sentBy`)}{" "}
            {eventDelivery ? <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.workflow(summary.slug, eventDelivery.workflow_id)}>{t(`${P}.sentByLink`)}</AppLink> : null}
          </p>
        ) : null}
        {receivesEvents(mode) && deliveryPaused(eventDelivery) ? <div className="mt-2"><EventDeliveryNotice delivery={eventDelivery} compact /></div> : null}
        {mode === "workflows" ? null : <p className="mt-1 text-xs text-fg-subtle">{t(`${P}.connectorAuthHint`)}</p>}
      </div>
      {mode === "workflows" ? null : (
        <fieldset>
          <legend className="label">{t("settings.webhooks.events")}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {WEBHOOK_EVENTS.map((key) => (
              <div key={key} className="rounded-md border border-line px-3 py-2">
                <Checkbox checked={events.includes(key)} onCheckedChange={(checked) => { onEventsChange(toggle(events, key, checked === true, WEBHOOK_EVENTS)); }} label={<span className="font-mono text-xs">{key}</span>} />
              </div>
            ))}
          </div>
          {events.length === 0 ? (
            <p role="alert" className="mt-1.5 text-xs font-semibold text-danger">
              {t(`${P}.eventsRequired`)}
            </p>
          ) : null}
        </fieldset>
      )}
      {mode === "filtered" ? (
        <div className="space-y-3 rounded-md bg-surface-2 p-3">
          <ChoiceChips legend={t(`${P}.channels`)} options={WEBHOOK_FILTER_CHANNELS} value={filters.channels} label={(c) => t(`engage.channels.${c}`)} onChange={(channels) => { onFiltersChange({ ...filters, channels }); }} />
          <ChoiceChips legend={t(`${P}.roles`)} options={WEBHOOK_FILTER_ROLES} value={filters.roles} label={(r) => t(`${P}.roleNames.${r}`)} onChange={(roles) => { onFiltersChange({ ...filters, roles }); }} />
          <p className="text-xs text-fg-subtle">{t(`${P}.filtersHint`)}</p>
        </div>
      ) : null}
    </div>
  );
}
